import React, { useState, useEffect } from 'react';
import { Table, Badge, Button, Form } from 'react-bootstrap';
import Select from 'react-select';

import { Team } from '../types/Team';
import ShimmerLoader from './ShimmerLoader';
import './TeamsTable.css';
import '../components/PlayerComponents.css';
import { getImageUrl } from '../utils/imageUrl';

interface TeamsTableProps {
  teams: Team[];
  loading?: boolean;
  showCheckboxes?: boolean;
  singleSelection?: boolean; // New prop for single selection mode
  showAddPlayers?: boolean;
  showManagePlayers?: boolean;
  showViewDetails?: boolean;
  showEdit?: boolean;
  showDelete?: boolean;
  showPayNow?: boolean; // New prop for Pay Now button
  isRecordPayment?: boolean; // New prop to control button text (Pay Now vs Record Payment)
  isRecordPaymentLoading?: boolean; // New prop for record payment loading state
  recordPaymentLoadingTeamId?: string | null; // New prop: per-row loading state
  showPaymentStats?: boolean; // New prop to control payment stats display
  onTeamSelect?: (teamId: string, selected: boolean) => void;
  onSelectAll?: (selected: boolean) => void;
  onAddPlayers?: (teamId: string) => void;
  onManagePlayers?: (teamId: string) => void;
  onViewDetails?: (team: Team) => void;
  onEdit?: (team: Team) => void;
  onDelete?: (teamId: string) => void;
  onPayNow?: (team: Team) => void; // New handler for Pay Now button
  searchTerm?: string;
  onSearchChange?: (searchTerm: string) => void;
  ageGroupFilter?: string;
  onAgeGroupFilterChange?: (filter: string) => void;
  showFilters?: boolean;
  showSearch?: boolean;
  title?: string;
  emptyMessage?: string;
  emptySubMessage?: string;
  externalFilters?: React.ReactNode;
  ageCategories?: string[];
  // New prop for conditional action button visibility based on registration status
  conditionalActionVisibility?: boolean;
}

const TeamsTable: React.FC<TeamsTableProps> = ({
  teams,
  loading = false,
  showCheckboxes = false,
  singleSelection = false, // New prop for single selection mode
  showAddPlayers = false,
  showManagePlayers = false,
  showViewDetails = false,
  showEdit = false,
  showDelete = false,
  showPayNow = false, // New prop for Pay Now button
  isRecordPayment = false, // New prop to control button text
  isRecordPaymentLoading = false, // New prop for record payment loading state
  recordPaymentLoadingTeamId = null, // New prop: per-row loading state
  showPaymentStats = true, // Default to true
  onTeamSelect,
  onSelectAll,
  onAddPlayers,
  onManagePlayers,
  onViewDetails,
  onEdit,
  onDelete,
  onPayNow, // New handler for Pay Now button
  searchTerm = '',
  onSearchChange,
  ageGroupFilter = 'all',
  onAgeGroupFilterChange,
  showFilters = true,
  showSearch = true,
  title = 'Team Lineups',
  emptyMessage = 'No teams found',
  emptySubMessage = 'No teams available. Create a new team to get started.',
  externalFilters,
  ageCategories = [],
  // New prop for conditional action button visibility based on registration status
  conditionalActionVisibility = false
}) => {
  const [filteredTeams, setFilteredTeams] = useState<Team[]>(teams);

  // Age group options for react-select - use dynamic data if available, fallback to default
  const ageGroupOptions = [
    { value: 'all', label: 'All' },
    ...(ageCategories.length > 0 
      ? ageCategories.map(category => ({ value: category, label: category }))
      : [
          { value: 'U11', label: 'U11' },
          { value: 'U12', label: 'U12' },
          { value: 'U13', label: 'U13' },
          { value: 'U14', label: 'U14' },
          { value: 'U15', label: 'U15' },
          { value: 'U16', label: 'U16' },
          { value: 'U17', label: 'U17' },
          { value: 'U18', label: 'U18' }
        ]
    )
  ];

  useEffect(() => {
    filterTeams();
    
    // Filter teams based on search and age group
  }, [teams, searchTerm, ageGroupFilter]);

  const filterTeams = () => {
    let filtered = teams;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(team =>
        team.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        team.shortName.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply age group filter - handle both 'All' and 'all' values
    if (ageGroupFilter && ageGroupFilter !== 'all' && ageGroupFilter !== 'All') {
      filtered = filtered.filter(team => team.ageGroups.includes(ageGroupFilter));
    }

    setFilteredTeams(filtered);
  };

  const handleTeamSelection = (teamId: string) => {
    if (onTeamSelect) {
      const team = teams.find(t => t._id === teamId);
      if (team) {
        // For single selection, always pass true (selected) since we handle deselection in parent
        onTeamSelect(teamId, !team.isSelected);
      }
    }
  };

  const handleSelectAll = () => {
    if (onSelectAll) {
      const allSelected = teams.every(team => team.isSelected);
      onSelectAll(!allSelected);
    }
  };

  const handleAddPlayers = (e: React.MouseEvent, teamId: string) => {
    e.preventDefault();
    e.stopPropagation();
    if (onAddPlayers) {
      onAddPlayers(teamId);
    }
  };

  const handleManagePlayers = (e: React.MouseEvent, teamId: string) => {
    e.preventDefault();
    e.stopPropagation();
    if (onManagePlayers) {
      onManagePlayers(teamId);
    }
  };

  const handleViewDetails = (e: React.MouseEvent, team: Team) => {
    e.preventDefault();
    e.stopPropagation();
    if (onViewDetails) {
      onViewDetails(team);
    }
  };

  const handleEdit = (e: React.MouseEvent, team: Team) => {
    e.preventDefault();
    e.stopPropagation();
    if (onEdit) {
      onEdit(team);
    }
  };

  const handleDelete = (e: React.MouseEvent, teamId: string) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Find the team to check if it has players
    const team = teams.find(t => t._id === teamId);
    if (team && (team.players > 0 || team.verifiedPlayers > 0)) {
      // Don't allow deletion if team has players
      return;
    }
    
    if (onDelete) {
      onDelete(teamId);
    }
  };

  const handlePayNow = (e: React.MouseEvent, team: Team) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Don't proceed if team has no players
    if (team.players === 0) {
      return;
    }
    
    // ✅ UNIFIED: Apply same logic as record payment - always allow payment if team has players
    // Removed the unpaid players check to match record payment behavior
    console.log('🚀 TeamsTable handlePayNow called for team:', team.name);
    
    if (onPayNow) {
      onPayNow(team);
    }
  };

  // Helper function to check if any player is not paid
  const hasUnpaidPlayers = (team: Team): boolean => {
    // First check if we have detailed player information
    if (team.playerDetails && team.playerDetails.length > 0) {
      // Check if any player has amount due greater than 0
      return team.playerDetails.some(player => (player.amountDue || 0) > 0);
    }
    
    // Enhanced logic: use paid player IDs for more accurate checking
    if (team.paidPlayerIds && team.paidPlayerIds.length >= 0) {
      // If there are players but not all are paid, there are unpaid players
      return team.players > team.paidPlayerIds.length;
    }
    
    // Fallback: compare total players vs paid players
    // If there are more total players than paid players, there are unpaid players
    if (team.players > 0 && team.paidPlayers !== undefined) {
      return team.players > team.paidPlayers;
    }
    
    // Additional fallback: check team-level payment status
    return team.paymentStatus === 'pending' || (!team.paymentStatus && team.paidPlayers === 0);
  };

  if (loading) {
    return (
      <div className="teams-table-container">
        {/* Filters Section - Only show if no external filters provided */}
        {showFilters && !externalFilters && (
          <div className="filters-section">
            <div className="d-flex justify-content-between align-items-center">
              <div className="filters-left">
                {showSearch && onSearchChange && (
                  <div className="search-input-wrapper">
                    <input
                      type="text"
                      className="form-search"
                      placeholder="Search Teams"
                      value={searchTerm}
                      onChange={(e) => onSearchChange(e.target.value)}
                      style={{ width: '240px', paddingRight: '35px' }}
                    />
                    {searchTerm && (
                      <button
                        type="button"
                        className="clear-search-btn"
                        onClick={() => onSearchChange('')}
                        aria-label="Clear search"
                      >
                        <i className="ri-close-line"></i>
                      </button>
                    )}
                  </div>
                )}
              </div>
              <div className="filters-right">
                {onAgeGroupFilterChange && (
                  <Select
                    classNamePrefix="react-select"
                    value={ageGroupOptions.find(option => option.value === ageGroupFilter)}
                    onChange={(selectedOption) => onAgeGroupFilterChange(selectedOption?.value || 'all')}
                    options={ageGroupOptions}
                    placeholder="All"
                    styles={{
                      control: (base) => ({ 
                        ...base, 
                        minHeight: '40px', 
                        width: '160px',
                        border: '1px solid #e0e0e0',
                        borderRadius: '8px',
                        boxShadow: 'none'
                      }),
                      menu: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                    isSearchable={false}
                  />
                )}
              </div>
            </div>
          </div>
        )}

        {/* Table Section */}
        <div className="table-section">
          <h5 className="table-title mb-3">{title}</h5>
          <ShimmerLoader type="table-rows" variant="team" rows={5} />
        </div>
      </div>
    );
  }

  return (
    <div className="teams-table-container">
      {/* Filters Section - Only show if no external filters provided */}
      {showFilters && !externalFilters && (
        <div className="filters-section">
          <div className="d-flex justify-content-between align-items-center">
            <div className="filters-left">
              {showSearch && onSearchChange && (
                <div className="search-input-wrapper">
                  <input
                    type="text"
                    className="form-search"
                    placeholder="Search Teams"
                    value={searchTerm}
                    onChange={(e) => onSearchChange(e.target.value)}
                    style={{ width: '240px', paddingRight: '35px' }}
                  />
                  {searchTerm && (
                    <button
                      type="button"
                      className="clear-search-btn"
                      onClick={() => onSearchChange('')}
                      title="Clear search"
                      style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#6c757d', cursor: 'pointer', padding: '4px', borderRadius: '4px', transition: 'color 0.2s ease' }}
                    >
                      <i className="ri-close-line"></i>
                    </button>
                  )}
                </div>
              )}
            </div>
            <div className="filters-right">
              {onAgeGroupFilterChange && (
                <Select
                  classNamePrefix="react-select"
                  value={ageGroupOptions.find(option => option.value === ageGroupFilter)}
                  onChange={(selectedOption) => onAgeGroupFilterChange(selectedOption?.value || 'all')}
                  options={ageGroupOptions}
                  placeholder="All"
                  styles={{
                    control: (base) => ({ 
                      ...base, 
                      minHeight: '40px', 
                      width: '160px',
                      border: '1px solid #e0e0e0',
                      borderRadius: '8px',
                      boxShadow: 'none'
                    }),
                    menu: (base) => ({ ...base, zIndex: 9999 }),
                  }}
                  isSearchable={false}
                />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Table Section */}
      <div className="table-section">
        <h5 className="table-title mb-3">{title}</h5>
        <div className="table-responsive">
          <Table className="teams-table">
            <thead>
              <tr>
                {showCheckboxes && !singleSelection && (
                  <th>
                    <Form.Check
                      type="checkbox"
                      checked={teams.filter(team => team.players > 0).every(team => team.isSelected)}
                      onChange={handleSelectAll}
                      disabled={teams.filter(team => team.players > 0).length === 0}
                    />
                  </th>
                )}
                {showCheckboxes && singleSelection && (
                  <th>
                    <span style={{ fontSize: '12px', color: '#6c757d' }}>Select</span>
                  </th>
                )}
                <th>Logo</th>
                <th>Team Name</th>
                <th>Abbreviation</th>
                <th>Age Categories</th>
                <th>Players</th>
                <th>Verify Status</th>
                {showPaymentStats && <th>Total Amount</th>}
                {showPaymentStats && <th>Amount Paid</th>}
                {showPaymentStats && <th>Amount Due</th>}
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredTeams.map((team) => {
                const thisRowLoading = recordPaymentLoadingTeamId ? (recordPaymentLoadingTeamId === team._id) : isRecordPaymentLoading;
                // ✅ UNIFIED: Apply same logic as record payment - only check if players exist
                const payNowDisabled = (team.players === 0 || thisRowLoading);
                return (
                  <tr key={team._id}>
                    {showCheckboxes && !singleSelection && (
                      <td>
                        <Form.Check
                          type="checkbox"
                          checked={team.isSelected || false}
                          onChange={() => handleTeamSelection(team._id)}
                          disabled={team.players === 0}
                        />
                      </td>
                    )}
                    {showCheckboxes && singleSelection && (
                      <td>
                        <Form.Check
                          type="radio"
                          name="team-selection"
                          checked={team.isSelected || false}
                          onChange={() => handleTeamSelection(team._id)}
                          disabled={team.players === 0}
                        />
                      </td>
                    )}
                    <td>
                      <div className="team-logo-small">
                        {team.logo ? (
                          <img 
                            src={getImageUrl(team.logo)}
                            alt={team.name}
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              console.log('🔍 Image error for team:', team.name, 'logo:', team.logo);
                              target.style.display = 'none';
                              target.nextElementSibling?.classList.remove('d-none');
                            }}
                            onLoad={(e) => {
        
                            }}
                          />
                        ) : null}
                        {!team.logo && (
                          <div className="default-team-logo-small">
                            <i className="ri-team-line"></i>
                          </div>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="team-name-cell">
                        <span className="team-name">{team.name}</span>
                        {team.players === 0 && (
                          <span className="no-players-indicator">(No players added)</span>
                        )}
                      </div>
                    </td>
                    <td>{team.shortName}</td>
                    <td>
                      <div className="age-categories-display">
                        {team.ageGroups.map((ageGroup, index) => (
                          <Badge key={index} bg="primary" className="me-1">
                            {ageGroup}
                          </Badge>
                        ))}
                      </div>
                    </td>
                    <td>{team.players} Players</td>
                    <td>
                      {team.verifiedPlayers && team.verifiedPlayers > 0 ? (
                        <span className="verify-stat">{team.verifiedPlayers} verified</span>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    {showPaymentStats && (
                      <td>
                        <span className="amount-display">
                          ₹{(team.totalAmount || 0).toLocaleString()}
                        </span>
                      </td>
                    )}
                    {showPaymentStats && (
                      <td>
                        <span className="amount-display">
                          ₹{(team.amountPaid || 0).toLocaleString()}
                          </span>
                      </td>
                        )}
                    {showPaymentStats && (
                      <td>
                        <span className="amount-display">
                          ₹{(team.balanceAmount || 0).toLocaleString()}
                        </span>
                      </td>
                    )}
                    <td>
                      <div className="action-buttons">
                        {showAddPlayers && team.players === 0 && (
                          <Button
                            variant="link"
                            size="sm"
                            onClick={(e) => handleAddPlayers(e, team._id)}
                            className="btn-link"
                          >
                            Add Players
                          </Button>
                        )}
                        {showManagePlayers && team.players > 0 && (
                          <Button
                            variant="link"
                            size="sm"
                            onClick={(e) => handleManagePlayers(e, team._id)}
                            className="btn-link"
                          >
                            Manage Players
                          </Button>
                        )}
                        {showViewDetails && (
                          <Button
                            variant="link"
                            size="sm"
                            onClick={(e) => handleViewDetails(e, team)}
                            className="btn-link"
                          >
                            View Details
                          </Button>
                        )}
                        {showEdit && (
                          <Button
                            variant="link"
                            size="sm"
                            className="btn-edit"
                            title="Edit Team"
                            onClick={(e) => handleEdit(e, team)}
                          >
                            <i className="ri-edit-line"></i>
                          </Button>
                        )}
                        {showDelete && (
                          <Button
                            variant="link"
                            size="sm"
                            className={`btn-delete ${(team.players > 0 || team.verifiedPlayers > 0) ? 'disabled' : ''}`}
                            title={(team.players > 0 || team.verifiedPlayers > 0) ? "Cannot delete team with players" : "Delete Team"}
                            onClick={(e) => handleDelete(e, team._id)}
                            disabled={team.players > 0 || team.verifiedPlayers > 0}
                          >
                            <i className="ri-delete-bin-line"></i>
                          </Button>
                        )}
                        {showPayNow && (
                           <Button
                             variant="link"
                             size="sm"
                             className={`btn-pay-now ${payNowDisabled ? 'disabled' : ''}`}
                             title={thisRowLoading ? "Loading payment preview..." : team.players === 0 ? "No players to pay for" : isRecordPayment ? "Record Payment" : "Pay Now"}
                             onClick={(e) => handlePayNow(e, team)}
                             disabled={payNowDisabled}
                           >
                             {thisRowLoading ? (
                               <>
                                 <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                                 Loading...
                               </>
                             ) : (
                               isRecordPayment ? 'Record Payment' : 'Pay Now'
                             )}
                           </Button>
                         )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
      </div>

      {filteredTeams.length === 0 && (
        <div className="text-center py-4">
          <i className="ri-team-line empty-icon"></i>
          <h5>{emptyMessage}</h5>
          <p className="text-muted">
            {searchTerm || ageGroupFilter !== 'all' 
              ? 'Try adjusting your search or filter criteria'
              : emptySubMessage
            }
          </p>
        </div>
      )}
    </div>
  );
};

export default TeamsTable; 